import { Pencil2Icon } from '@radix-ui/react-icons';
import { Button, Flex, Heading, Skeleton, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonHitterAvatar } from 'components/common/hitters/avatar';
import { HitterHeightBadge } from 'components/common/hitters/hitter-height-badge';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { PlayerStatusBadge } from 'components/common/hitters/player-status-badge';
import { SectionHeader } from 'components/sections/header';
import useHitterProfileStore from 'components/sections/hitter-library/profile/use-hitter-profile-store';
import { HitterLibraryTeamLogo } from 'components/sections/hitter-library/team-logo';
import { HittersContext } from 'contexts/hitters.context';
import { CrudAction } from 'enums/tables';
import { t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';

const SHOW_TEAM_NAME = false;

export const HitterProfileHeader = (props: IVerboseComponent) => {
  const navigate = useNavigate();
  const hitter = useHitterProfileStore((s) => s.hitter);

  const { openCrudDialog } = useContext(HittersContext);

  const teamOrLevelLabel = useMemo(() => {
    if (!hitter) {
      return undefined;
    }

    if (SHOW_TEAM_NAME) {
      const codeName = [
        hitter._mx_mlb_team_details?.code ?? '',
        hitter._mx_mlb_team_details?.shortName ?? '',
      ]
        .join(' ')
        .trim();

      if (codeName) {
        return codeName;
      }

      if (hitter._mx_mlb_team_details?.name) {
        return hitter._mx_mlb_team_details.name;
      }
    }

    // fallback
    return hitter.level;
  }, [hitter]);

  return (
    <ErrorBoundary identifier={props.identifier}>
      <SectionHeader
        header={null}
        breadcrumbs={[
          {
            label: 'main.hitters',
            onClick: () => {
              useHitterProfileStore
                .getState()
                .mergeHitsFilter({ hitterID: '' });

              navigate(paths.hitterLibrary.hitters);
            },
          },
          ...(hitter ? [{ label: hitter.name }] : []),
        ]}
      />

      <Flex
        align="center"
        justify={{ xs: 'start', sm: 'between' }}
        wrap="wrap"
        gap={RADIX.FLEX.GAP.SM}
      >
        <Flex gap={RADIX.FLEX.GAP.MD} align="center">
          <Skeleton loading={!hitter}>
            {hitter && (
              <CommonHitterAvatar
                // w/o this key, this renders previous avatar if latest selection has no avatar
                key={hitter._id}
                hitter={hitter}
                size="7"
                clickDisabled
              />
            )}
          </Skeleton>
          <Flex direction="column" justify="center" gap={RADIX.FLEX.GAP.XS}>
            <Flex align="center" gap="2">
              <Skeleton loading={!hitter}>
                <Heading size={RADIX.HEADING.SIZE.LG}>
                  {hitter?.name ?? t('hitters.anonymous')}
                </Heading>
              </Skeleton>

              <Skeleton loading={!hitter}>
                {hitter && <HitterSideBadge value={hitter.side} />}
              </Skeleton>

              <Skeleton loading={!hitter}>
                {hitter?.height_ft && (
                  <HitterHeightBadge heightFt={hitter.height_ft} />
                )}
              </Skeleton>

              <Skeleton loading={!hitter}>
                <PlayerStatusBadge value={hitter?.statsApiData?.statusCode} />
              </Skeleton>
            </Flex>

            <Skeleton loading={!hitter}>
              <Flex gap="2">
                {hitter && (
                  <HitterLibraryTeamLogo size="20px" hitter={hitter} />
                )}

                {teamOrLevelLabel && (
                  <Text color={RADIX.COLOR.SECONDARY} truncate>
                    {teamOrLevelLabel}
                  </Text>
                )}
              </Flex>
            </Skeleton>
          </Flex>
        </Flex>

        <Flex align="center" gap="2">
          <Button
            onClick={() => {
              if (!hitter) {
                return;
              }

              openCrudDialog({
                key: Date.now(),
                action: CrudAction.Update,
                models: [hitter],
              });
            }}
          >
            <Pencil2Icon /> {t('hitters.edit-hitter')}
          </Button>
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
